/* ============================================
   CHAPTER NAVIGATION COMPONENT
   The Creative Orchestra - GEMINI Framework
   Navigation between chapters (prev/next)
   ============================================ */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Home, List } from 'lucide-react';
import { chapterList, getChapterById } from '@data';
import { getNextChapterId, getPrevChapterId } from '@utils/helpers';

/**
 * Chapter Navigation Component
 * Shows prev/next chapter links with titles
 * 
 * @param {Object} props
 * @param {string} props.currentChapterId - Current chapter ID
 * @param {Function} props.onOpenToc - Callback to open table of contents
 * @param {string} props.className - Additional CSS classes
 */
const ChapterNavigation = ({ 
  currentChapterId,
  onOpenToc,
  className = '',
}) => {
  // Get adjacent chapter IDs
  const prevChapterId = useMemo(() => 
    getPrevChapterId(currentChapterId), [currentChapterId]
  );
  const nextChapterId = useMemo(() => 
    getNextChapterId(currentChapterId), [currentChapterId]
  );

  // Get chapter data
  const prevChapter = useMemo(() => 
    prevChapterId ? getChapterById(prevChapterId) : null, [prevChapterId]
  );
  const nextChapter = useMemo(() => 
    nextChapterId ? getChapterById(nextChapterId) : null, [nextChapterId]
  );

  return (
    <nav className={`${className}`}>
      {/* Mobile navigation */}
      <div className="md:hidden">
        <MobileChapterNav
          prevChapter={prevChapter}
          nextChapter={nextChapter}
          onOpenToc={onOpenToc}
        />
      </div>

      {/* Desktop navigation */}
      <div className="hidden md:block">
        <DesktopChapterNav
          prevChapter={prevChapter}
          nextChapter={nextChapter}
          onOpenToc={onOpenToc}
        />
      </div>
    </nav>
  );
};

/**
 * Desktop Chapter Navigation
 */
const DesktopChapterNav = ({ prevChapter, nextChapter, onOpenToc }) => {
  return (
    <div className="flex items-stretch justify-between gap-4">
      {/* Previous chapter */}
      <div className="flex-1">
        {prevChapter ? (
          <Link to={`/novel/${prevChapter.id}`}>
            <motion.div
              className="h-full p-6 rounded-xl bg-white/5 border border-white/10
                hover:bg-white/10 hover:border-white/20 transition-all
                group cursor-pointer"
              whileHover={{ x: -5 }}
            >
              <div className="flex items-center gap-2 text-gray-500 mb-2">
                <ChevronLeft className="w-4 h-4 group-hover:text-indigo-400 transition-colors" />
                <span className="text-sm">Previous</span>
              </div>
              <p className="text-white font-medium group-hover:text-indigo-300 transition-colors">
                {prevChapter.title}
              </p>
              {prevChapter.type !== 'chapter' && (
                <p className="text-xs text-gray-500 mt-1 capitalize">
                  {prevChapter.type}
                </p>
              )}
            </motion.div>
          </Link>
        ) : (
          <div className="h-full p-6 rounded-xl bg-white/5 border border-white/5 opacity-50">
            <div className="flex items-center gap-2 text-gray-600 mb-2">
              <ChevronLeft className="w-4 h-4" />
              <span className="text-sm">Previous</span>
            </div>
            <p className="text-gray-600">Start of novel</p>
          </div>
        )}
      </div>

      {/* Center actions */}
      <div className="flex flex-col items-center justify-center gap-2">
        {onOpenToc && (
          <motion.button
            onClick={onOpenToc}
            className="p-3 rounded-lg bg-white/5 border border-white/10
              hover:bg-white/10 hover:border-white/20 transition-all
              text-gray-400 hover:text-white"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title="Table of Contents"
          >
            <List className="w-5 h-5" />
          </motion.button>
        )}
        <Link to="/">
          <motion.button
            className="p-3 rounded-lg bg-white/5 border border-white/10
              hover:bg-white/10 hover:border-white/20 transition-all
              text-gray-400 hover:text-white"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title="Home"
          >
            <Home className="w-5 h-5" />
          </motion.button>
        </Link>
      </div>

      {/* Next chapter */}
      <div className="flex-1">
        {nextChapter ? (
          <Link to={`/novel/${nextChapter.id}`}>
            <motion.div
              className="h-full p-6 rounded-xl bg-white/5 border border-white/10
                hover:bg-white/10 hover:border-white/20 transition-all
                group cursor-pointer text-right"
              whileHover={{ x: 5 }}
            >
              <div className="flex items-center justify-end gap-2 text-gray-500 mb-2">
                <span className="text-sm">Next</span>
                <ChevronRight className="w-4 h-4 group-hover:text-indigo-400 transition-colors" />
              </div>
              <p className="text-white font-medium group-hover:text-indigo-300 transition-colors">
                {nextChapter.title}
              </p>
              {nextChapter.type !== 'chapter' && (
                <p className="text-xs text-gray-500 mt-1 capitalize">
                  {nextChapter.type}
                </p>
              )}
            </motion.div>
          </Link>
        ) : (
          <div className="h-full p-6 rounded-xl bg-white/5 border border-white/5 
            opacity-50 text-right">
            <div className="flex items-center justify-end gap-2 text-gray-600 mb-2">
              <span className="text-sm">Next</span>
              <ChevronRight className="w-4 h-4" />
            </div>
            <p className="text-gray-600">End of novel</p>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Mobile Chapter Navigation
 */
const MobileChapterNav = ({ prevChapter, nextChapter, onOpenToc }) => {
  return (
    <div className="flex items-center justify-between gap-2">
      {/* Previous */}
      {prevChapter ? (
        <Link 
          to={`/novel/${prevChapter.id}`}
          className="flex-1"
        >
          <motion.button
            className="w-full flex items-center gap-2 px-4 py-3 rounded-lg
              bg-white/5 border border-white/10 text-left
              hover:bg-white/10 transition-colors"
            whileTap={{ scale: 0.95 }}
          >
            <ChevronLeft className="w-5 h-5 text-gray-400" />
            <div className="truncate">
              <span className="text-xs text-gray-500 block">Prev</span>
              <span className="text-sm text-white truncate">{prevChapter.title}</span>
            </div>
          </motion.button>
        </Link>
      ) : (
        <div className="flex-1" />
      )}

      {/* Center actions */}
      <div className="flex items-center gap-1">
        {onOpenToc && (
          <motion.button
            onClick={onOpenToc}
            className="p-2 rounded-lg bg-white/5 border border-white/10
              text-gray-400"
            whileTap={{ scale: 0.9 }}
          >
            <List className="w-5 h-5" />
          </motion.button>
        )}
        <Link to="/">
          <motion.button
            className="p-2 rounded-lg bg-white/5 border border-white/10
              text-gray-400"
            whileTap={{ scale: 0.9 }}
          >
            <Home className="w-5 h-5" />
          </motion.button>
        </Link>
      </div>

      {/* Next */}
      {nextChapter ? (
        <Link 
          to={`/novel/${nextChapter.id}`}
          className="flex-1"
        >
          <motion.button
            className="w-full flex items-center justify-end gap-2 px-4 py-3 rounded-lg
              bg-white/5 border border-white/10 text-right
              hover:bg-white/10 transition-colors"
            whileTap={{ scale: 0.95 }}
          >
            <div className="truncate">
              <span className="text-xs text-gray-500 block">Next</span>
              <span className="text-sm text-white truncate">{nextChapter.title}</span>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </motion.button>
        </Link>
      ) : (
        <div className="flex-1" />
      )}
    </div>
  );
};

/**
 * Floating Chapter Navigation
 * Fixed position prev/next buttons
 */
export const FloatingChapterNav = ({ currentChapterId }) => {
  const prevChapterId = getPrevChapterId(currentChapterId);
  const nextChapterId = getNextChapterId(currentChapterId);

  return (
    <>
      {/* Previous */}
      {prevChapterId && (
        <Link to={`/novel/${prevChapterId}`}>
          <motion.button
            className="fixed left-4 top-1/2 -translate-y-1/2 z-40
              p-3 rounded-full bg-black/60 backdrop-blur-sm
              border border-white/10 text-white/60
              hover:text-white hover:bg-black/80 hover:border-white/20
              transition-all hidden lg:block"
            whileHover={{ scale: 1.1, x: -3 }}
            whileTap={{ scale: 0.9 }}
            title="Previous chapter"
          >
            <ChevronLeft className="w-6 h-6" />
          </motion.button>
        </Link>
      )}

      {/* Next */}
      {nextChapterId && (
        <Link to={`/novel/${nextChapterId}`}>
          <motion.button
            className="fixed right-4 top-1/2 -translate-y-1/2 z-40
              p-3 rounded-full bg-black/60 backdrop-blur-sm
              border border-white/10 text-white/60
              hover:text-white hover:bg-black/80 hover:border-white/20
              transition-all hidden lg:block"
            whileHover={{ scale: 1.1, x: 3 }}
            whileTap={{ scale: 0.9 }}
            title="Next chapter"
          >
            <ChevronRight className="w-6 h-6" />
          </motion.button>
        </Link>
      )}
    </>
  );
};

/**
 * Chapter Progress Text
 * Shows current chapter position
 */
export const ChapterProgress = ({ currentChapterId, className = '' }) => {
  const currentIndex = chapterList.findIndex(ch => ch.id === currentChapterId);
  const total = chapterList.length;

  if (currentIndex === -1) return null;

  return (
    <span className={`text-sm text-gray-500 ${className}`}>
      {currentIndex + 1} of {total}
    </span>
  );
};

export default ChapterNavigation;
